import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Search, Send, History, MessageSquare, CheckSquare, Square, Users, Check, X, Loader2 } from 'lucide-react';
import { adminAPI } from '../../../services/api';
import api from '../../../services/api';

const campaignAPI = {
  merchants: (params) => api.get('/admin/campaign/merchants', { params }),
  sendSMS:   (body)   => api.post('/admin/campaign/sms', body),
  history:   (params) => api.get('/admin/campaign/history', { params: { ...params, type: 'sms' } }),
};

const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1', borderSoft: '#f0f0f6',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  success: '#10B981', successSoft: '#D1FAE5',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  danger: '#EF4444', dangerSoft: '#FEE2E2',
  radius: 14, radiusSm: 8,
};

const StatusBadge = ({ status }) => {
  const map = {
    completed: { bg: C.successSoft, color: C.success },
    sending:   { bg: C.primarySoft, color: C.primary },
    failed:    { bg: C.dangerSoft,  color: C.danger  },
    draft:     { bg: C.borderSoft,  color: C.muted   },
  };
  const s = map[status] || map.draft;
  return <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{status}</span>;
};

export default function AdminSMSCampaign() {
  const [tab, setTab]               = useState('send');
  const [merchants, setMerchants]   = useState([]);
  const [selected, setSelected]     = useState([]);
  const [recipientType, setRecipientType] = useState('all');
  const [search, setSearch]         = useState('');
  const [title, setTitle]           = useState('');
  const [message, setMessage]       = useState('');
  const [sending, setSending]       = useState(false);
  const [history, setHistory]       = useState([]);
  const [loadingM, setLoadingM]     = useState(false);

  useEffect(() => {
    if (tab === 'send') fetchMerchants();
    if (tab === 'history') fetchHistory();
  }, [tab]);

  useEffect(() => {
    const t = setTimeout(fetchMerchants, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchMerchants = async () => {
    setLoadingM(true);
    try {
      const r = await campaignAPI.merchants({ search });
      setMerchants(r.data?.data || []);
    } catch (_) {}
    finally { setLoadingM(false); }
  };

  const fetchHistory = async () => {
    try {
      const r = await campaignAPI.history();
      setHistory(r.data?.data?.data || r.data?.data || []);
    } catch (_) {}
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll    = () => setSelected(s => s.length === merchants.length ? [] : merchants.map(m => m.id));

  const handleSend = async () => {
    if (!title.trim()) { toast.error('Campaign title required'); return; }
    if (!message.trim()) { toast.error('Message required'); return; }
    if (recipientType === 'selected' && selected.length === 0) { toast.error('Select at least one merchant'); return; }

    setSending(true);
    try {
      const r = await campaignAPI.sendSMS({
        title, message,
        recipient_type: recipientType,
        recipient_ids:  recipientType === 'selected' ? selected : [],
      });
      toast.success(r.data?.message || 'SMS campaign sent!');
      setTitle(''); setMessage(''); setSelected([]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send SMS');
    } finally { setSending(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>

      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: C.primarySoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MessageSquare size={18} color={C.primary} strokeWidth={2} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: '-.02em' }}>SMS Campaign</h1>
            <p style={{ margin: 0, fontSize: 13, color: C.muted }}>Send bulk SMS messages to your merchants.</p>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, padding: 4, width: 'fit-content' }}>
        {[
          { key: 'send',    label: 'Send Campaign', Icon: Send    },
          { key: 'history', label: 'History',        Icon: History },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ height: 34, padding: '0 16px', borderRadius: 6, border: 'none', background: tab === t.key ? C.primary : 'transparent', color: tab === t.key ? '#fff' : C.muted, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit', transition: 'all .15s' }}>
            <t.Icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── Send Tab ── */}
      {tab === 'send' && (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* Compose Card */}
          <div style={{ flex: 1, minWidth: 320, background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radius, padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, marginBottom: -6 }}>Compose Message</div>

            {/* Title */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.body, display: 'block', marginBottom: 6 }}>Campaign Title <span style={{ color: C.danger }}>*</span></label>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Festival Offer"
                style={{ width: '100%', padding: '10px 12px', border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, fontSize: 13, outline: 'none', color: C.ink, background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            </div>

            {/* Message */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.body, display: 'block', marginBottom: 6 }}>
                Message <span style={{ color: C.danger }}>*</span>
                <span style={{ fontWeight: 400, color: C.muted, marginLeft: 8 }}>{message.length}/160</span>
              </label>
              <textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Type your SMS message here…" rows={5} maxLength={160}
                style={{ width: '100%', padding: '10px 12px', border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, fontSize: 13, outline: 'none', color: C.ink, background: '#fff', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }} />
            </div>

            {/* Recipients */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.body, display: 'block', marginBottom: 8 }}>Recipients</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { val: 'all',      label: 'All Merchants' },
                  { val: 'selected', label: `Selected (${selected.length})` },
                ].map(t => (
                  <button key={t.val} onClick={() => setRecipientType(t.val)}
                    style={{ flex: 1, padding: '9px 14px', borderRadius: C.radiusSm, border: `1.5px solid ${recipientType === t.val ? C.primary : C.border}`, background: recipientType === t.val ? C.primarySoft : 'transparent', color: recipientType === t.val ? C.primary : C.body, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s' }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Send Button */}
            <button onClick={handleSend} disabled={sending}
              style={{ background: sending ? C.muted : C.primary, color: '#fff', border: 'none', borderRadius: C.radiusSm, padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: sending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'inherit', transition: 'background .15s' }}>
              {sending
                ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Sending…</>
                : <><Send size={15} /> {recipientType === 'selected' ? `Send to ${selected.length} Merchants` : 'Send to All Merchants'}</>}
            </button>
          </div>

          {/* Merchant Selector */}
          {recipientType === 'selected' && (
            <div style={{ width: 340, flexShrink: 0, background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radius, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>Select Merchants</div>
                <button onClick={toggleAll} style={{ fontSize: 12, color: C.primary, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontFamily: 'inherit' }}>
                  {selected.length === merchants.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {/* Search */}
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search merchants…"
                  style={{ width: '100%', padding: '9px 12px 9px 32px', border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, fontSize: 13, outline: 'none', color: C.ink, boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>

              <div style={{ fontSize: 11, color: C.muted }}>{merchants.length} merchants · {selected.length} selected</div>

              {/* List */}
              <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {loadingM
                  ? <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 13 }}>Loading…</div>
                  : merchants.length === 0
                    ? <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 13 }}>No merchants found</div>
                    : merchants.map(m => (
                      <div key={m.id} onClick={() => toggleSelect(m.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: C.radiusSm, cursor: 'pointer', border: `1.5px solid ${selected.includes(m.id) ? C.primary : C.borderSoft}`, background: selected.includes(m.id) ? C.primarySoft : 'transparent', transition: 'all .12s' }}>
                        <span style={{ color: selected.includes(m.id) ? C.primary : C.borderSoft, flexShrink: 0 }}>
                          {selected.includes(m.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.username}</div>
                          <div style={{ fontSize: 11, color: C.muted }}>{m.phone} · {m.email}</div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: m.account_status === 'active' ? C.success : C.warning, flexShrink: 0 }}>
                          {m.account_status}
                        </span>
                      </div>
                    ))
                }
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── History Tab ── */}
      {tab === 'history' && (
        <div style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radius, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.borderSoft}`, fontSize: 14, fontWeight: 700, color: C.ink }}>Campaign History</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  {['Title', 'Message', 'Recipients', 'Sent', 'Failed', 'Status', 'Date'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'left', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 48, color: C.muted, fontSize: 13 }}>No campaigns sent yet</td></tr>
                ) : history.map(c => (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: C.ink }}>{c.title}</td>
                    <td style={{ padding: '12px 16px', maxWidth: 200 }}><span style={{ fontSize: 12, color: C.muted }}>{c.message?.substring(0, 60)}{c.message?.length > 60 ? '…' : ''}</span></td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: C.body }}>{c.total_recipients}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: C.success }}>{c.sent_count}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: C.danger }}>{c.failed_count}</td>
                    <td style={{ padding: '12px 16px' }}><StatusBadge status={c.status} /></td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{fmtDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
